// src/components/MainApp.js
import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Chip, LinearProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import TopBar from './TopBar';
import { RoundedCard } from './shared/StyledComponents';
import PeopleIcon from '@mui/icons-material/People';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LoopIcon from '@mui/icons-material/Loop';
import RedeemIcon from '@mui/icons-material/Redeem';
import { useNavigate } from 'react-router-dom';
import { getToken, parseJwt } from '../lib/auth';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  backgroundColor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(4),
  },
}));

const StatCard = styled(RoundedCard)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #e8e8e8',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${fadeIn} 0.4s ease-out`,
  padding: theme.spacing(3),
  '&:hover': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
}));

const WelcomeBanner = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  borderRadius: 24,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.5s ease-out`,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-5%',
    width: 300,
    height: 300,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,183,27,0.15) 0%, transparent 70%)',
  },
}));

const IconCircle = styled(Box)(({ $bgColor = '#fff8e7' }) => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  backgroundColor: $bgColor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ActivityItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 0),
  borderBottom: '1px solid #f5f5f5',
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const MiniBar = styled(Box)(({ $height = 40, $color = '#ffb71b' }) => ({
  width: 8,
  height: $height,
  borderRadius: 4,
  backgroundColor: $color,
  transition: 'height 0.3s ease',
}));

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function statusLabel(status) {
  if (status === 'Claimed') return 'Redeemed reward';
  if (status === 'Completed') return 'Completed card';
  return 'Earned a stamp';
}

export default function MainApp() {
  const navigate = useNavigate();
  const token = getToken();
  const payload = token ? parseJwt(token) : null;
  const userId = payload?.sub;

  const [businessName, setBusinessName] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const authToken = localStorage.getItem('token');

    const fetchVendor = fetch(`/api/vendor/${userId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then(r => r.ok ? r.json() : null);

    const fetchStats = fetch(`/api/dashboard/stats/${userId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    }).then(r => r.ok ? r.json() : null);

    Promise.all([fetchVendor, fetchStats]).then(([vendorData, statsData]) => {
      if (vendorData?.vendor) setBusinessName(vendorData.vendor.company || '');
      if (statsData?.stats) setStats(statsData.stats);
    }).catch(() => {});
  }, [userId]);

  const totalCustomers = stats?.totalCustomers ?? 0;
  const rewardsRedeemed = stats?.rewardsRedeemed ?? 0;
  const activeNotifications = stats?.activeNotifications ?? 0;
  const repeatCustomers = stats?.repeatCustomers ?? 0;
  const weeklyData = stats?.weeklyData ?? [
    { day: 'Sun', value: 0 }, { day: 'Mon', value: 0 }, { day: 'Tue', value: 0 },
    { day: 'Wed', value: 0 }, { day: 'Thu', value: 0 }, { day: 'Fri', value: 0 },
    { day: 'Sat', value: 0 },
  ];
  const recentActivity = stats?.recentActivity ?? [];
  const completionRate = stats?.completionRate ?? 0;
  const retentionRate = stats?.retentionRate ?? 0;

  const statCards = [
    {
      label: 'Total Customers',
      value: totalCustomers.toLocaleString(),
      icon: <PeopleIcon sx={{ color: '#1976d2', fontSize: 24 }} />,
      iconBg: '#e3f2fd',
    },
    {
      label: 'Rewards Redeemed',
      value: rewardsRedeemed.toLocaleString(),
      icon: <CardGiftcardIcon sx={{ color: '#ff9800', fontSize: 24 }} />,
      iconBg: '#fff3e0',
    },
    {
      label: 'Active Notifications',
      value: activeNotifications.toString(),
      icon: <NotificationsActiveIcon sx={{ color: '#9c27b0', fontSize: 24 }} />,
      iconBg: '#f3e5f5',
    },
    {
      label: 'Repeat Customers',
      value: `${repeatCustomers}%`,
      icon: <LoopIcon sx={{ color: '#4caf50', fontSize: 24 }} />,
      iconBg: '#e8f5e9',
    },
  ];

  const quickActions = [
    { label: 'Set Up Rewards', path: 'rewards', icon: <RedeemIcon sx={{ fontSize: 20 }} /> },
    { label: 'View QR Code', path: 'qr-code', icon: <CardGiftcardIcon sx={{ fontSize: 20 }} /> },
    { label: 'Manage Notifications', path: 'notifications', icon: <NotificationsActiveIcon sx={{ fontSize: 20 }} /> },
  ];

  const maxVal = Math.max(...weeklyData.map(d => d.value), 1);

  return (
    <PageContainer>
      <TopBar />
      <ContentContainer maxWidth="xl">
        <WelcomeBanner>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: '#fff', mb: 1 }}>
              {businessName ? `Welcome back, ${businessName}` : 'Welcome to your Dashboard'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 600 }}>
              Here's an overview of your loyalty program. Set up your rewards, share your QR code, and start building customer loyalty.
            </Typography>
          </Box>
        </WelcomeBanner>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 3,
            mb: 4,
          }}
        >
          {statCards.map((stat, index) => (
            <StatCard key={index}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <IconCircle $bgColor={stat.iconBg}>
                  {stat.icon}
                </IconCircle>
              </Box>
              <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, fontWeight: 800, color: '#111', mb: 0.5 }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontWeight: 500 }}>
                {stat.label}
              </Typography>
            </StatCard>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            mb: 4,
          }}
        >
          <StatCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
                  Weekly Scans
                </Typography>
                <Typography variant="body2" sx={{ color: '#888' }}>
                  Customer QR code scans this week
                </Typography>
              </Box>
              <Chip
                label="This Week"
                size="small"
                sx={{ backgroundColor: '#f5f5f5', fontWeight: 500, fontSize: '0.75rem' }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: 160, px: 2 }}>
              {weeklyData.map((d, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <MiniBar $height={Math.max((d.value / maxVal) * 120, 8)} $color={d.value > 0 ? '#ffb71b' : '#e8e8e8'} />
                  <Typography variant="caption" sx={{ color: '#999', fontWeight: 500 }}>
                    {d.day}
                  </Typography>
                </Box>
              ))}
            </Box>
            {weeklyData.every(d => d.value === 0) && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#bbb' }}>
                  Scans will appear here once customers start using your QR code
                </Typography>
              </Box>
            )}
          </StatCard>

          <StatCard>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#222', mb: 3 }}>
              Quick Actions
            </Typography>
            {quickActions.map((action, index) => (
              <Box
                key={index}
                onClick={() => navigate(`/${action.path}/${userId}`)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  mb: 1.5,
                  borderRadius: 3,
                  border: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#ffb71b',
                    backgroundColor: '#fffdf5',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      backgroundColor: '#fff8e7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffb71b',
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#333' }}>
                    {action.label}
                  </Typography>
                </Box>
                <ArrowForwardIcon sx={{ color: '#ccc', fontSize: 20 }} />
              </Box>
            ))}
          </StatCard>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          <StatCard>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#222', mb: 2 }}>
              Recent Activity
            </Typography>
            {recentActivity.length === 0 ? (
              <ActivityItem>
                <Box
                  sx={{
                    width: 36, height: 36, borderRadius: 2,
                    backgroundColor: '#fff8e7', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <StarIcon sx={{ fontSize: 18, color: '#ffb71b' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#999' }}>
                    No activity yet
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#bbb' }}>
                    Customer activity will appear here
                  </Typography>
                </Box>
              </ActivityItem>
            ) : (
              recentActivity.map((activity, index) => (
                <ActivityItem key={index}>
                  <Box
                    sx={{
                      width: 36, height: 36, borderRadius: 2,
                      backgroundColor: '#fff8e7', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <StarIcon sx={{ fontSize: 18, color: '#ffb71b' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                      {activity.customerName} — {statusLabel(activity.status)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      {formatTimeAgo(activity.updatedAt)}
                    </Typography>
                  </Box>
                </ActivityItem>
              ))
            )}
          </StatCard>

          <StatCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <EmojiEventsIcon sx={{ color: '#ffb71b' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
                Loyalty Program Status
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>Reward Completion Rate</Typography>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: 700 }}>{completionRate}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={completionRate} sx={{ height: 8, borderRadius: 4, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#ffb71b' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>Customer Retention</Typography>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: 700 }}>{retentionRate}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={retentionRate} sx={{ height: 8, borderRadius: 4, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#4caf50' } }} />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>Notifications Active</Typography>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: 700 }}>{activeNotifications} / 4</Typography>
                </Box>
                <LinearProgress variant="determinate" value={activeNotifications * 25} sx={{ height: 8, borderRadius: 4, backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#1976d2' } }} />
              </Box>
            </Box>
          </StatCard>
        </Box>
      </ContentContainer>
    </PageContainer>
  );
}
